import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import Swal from 'sweetalert2';
import { PetsService } from '../../../../services/pets';
import { UserService } from '../../../../services/user';

@Component({
  selector: 'app-petsmobile',
  templateUrl: './petsmobile.component.html',
  styleUrl: './petsmobile.component.scss'
})
export class PetsmobileComponent implements OnInit {

  warehouseId!: number;
  clientId?: number;

  allData: any[] = [];
  visiblePets: any[] = [];

  contentReady = true;
  noData = false;

  petForm!: FormGroup;
  editMode = false;
  selectedPetId: number | null = null;

  pageSize = 10;
  currentIndex = 0;
  loadingMore = false;
  allLoaded = false;
  isSubAdmin = false;
  isAr: boolean = localStorage.getItem('language') === 'ar';

  constructor(
    private petsService: PetsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private transloco: TranslocoService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadUserRole();

    this.warehouseId = Number(localStorage.getItem('posId'));

    this.route.queryParams.subscribe(params => {
      this.clientId = params['id'] ? +params['id'] : undefined;
      this.loadPets();
    });
  }

  private loadUserRole(): void {
    this.userService.getAccountType().subscribe({
      next: (data) => {
        this.isSubAdmin = String(data?.user_type) === '6';
      },
      error: () => {
        this.isSubAdmin = false;
      }
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (this.contentReady || this.loadingMore || this.allLoaded || this.noData) return;

    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const bottomReached = scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  initForm(): void {
    this.petForm = this.fb.group({
      pet_type: ['', Validators.required],
      name: ['', Validators.required],
      breed: [''],
      color: [''],
      special_markings: [''],
      date_of_birth: [''],
      gender: ['male'],
      spayed_neutered: [false],
    });
  }

  loadPets(): void {
    this.contentReady = true;
    this.noData = false;
    this.visiblePets = [];
    this.currentIndex = 0;
    this.allLoaded = false;

    this.petsService.getAllPets(this.warehouseId, this.clientId).subscribe({
      next: (data) => {
        const raw = data.data || data;

        this.allData = (raw || []).map((p: any) => ({
          ...p,
          hasUpcomingVaccination: p?.hasUpcomingVaccination ?? false
        }));

        this.noData = this.allData.length === 0;
        this.contentReady = false;

        this.resetInfiniteList();
        this.markPetsWithUpcomingVaccinations();
      },

      error: (err) => {
        console.error(err);
        this.contentReady = false;
        this.noData = true;
      }
    });
  }

  resetInfiniteList(): void {
    this.visiblePets = [];
    this.currentIndex = 0;
    this.allLoaded = this.allData.length === 0;

    if (!this.allLoaded) {
      this.loadMore();
    }
  }

  loadMore(): void {
    if (this.loadingMore || this.allLoaded || this.contentReady) return;

    this.loadingMore = true;

    const nextItems = this.allData.slice(
      this.currentIndex,
      this.currentIndex + this.pageSize
    );

    if (nextItems.length === 0) {
      this.allLoaded = true;
      this.loadingMore = false;
      return;
    }

    this.visiblePets = [...this.visiblePets, ...nextItems];
    this.currentIndex += nextItems.length;
    this.allLoaded = this.currentIndex >= this.allData.length;

    setTimeout(() => {
      this.loadingMore = false;
    }, 150);
  }

  savePet(): void {
    if (this.petForm.invalid) {
      this.petForm.markAllAsTouched();
      return;
    }

    const payload: any = {
      ...this.petForm.value,
      warehouse_id: this.warehouseId
    };

    if (this.clientId) {
      payload.client_id = this.clientId;
    }

    if (this.editMode && this.selectedPetId) {
      this.petsService.updatePet(this.selectedPetId, payload).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'تم التحديث بنجاح',
            text: 'تم تعديل بيانات الحيوان بنجاح!'
          });

          this.resetForm();
          this.loadPets();
        },

        error: (err) => {
          console.error(err);
          Swal.fire('خطأ', 'حدث خطأ أثناء تعديل البيانات.', 'error');
        }
      });

      return;
    }

    this.petsService.createPet(payload).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'تم الحفظ بنجاح',
          text: 'تمت إضافة الحيوان بنجاح!'
        });

        this.resetForm();
        this.loadPets();
      },

      error: (err) => {
        console.error(err);
        Swal.fire('خطأ', 'تعذر إضافة الحيوان، حاول مرة أخرى.', 'error');
      }
    });
  }

  editPet(pet: any): void {
    if (!this.clientId) return;

    this.editMode = true;
    this.selectedPetId = pet.id;

    this.petForm.patchValue({
      pet_type: pet.pet_type ?? '',
      name: pet.name ?? '',
      breed: pet.breed ?? '',
      color: pet.color ?? '',
      special_markings: pet.special_markings ?? '',
      date_of_birth: pet.date_of_birth ?? '',
      gender: pet.gender ?? 'male',
      spayed_neutered: !!pet.spayed_neutered
    });

    setTimeout(() => {
      document.querySelector('.mobile-form-card')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }

  deletePet(id: number): void {
    Swal.fire({
      title: 'هل أنت متأكد؟',
      text: 'لن يمكنك التراجع بعد الحذف!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'نعم، احذف!',
      cancelButtonText: 'إلغاء'
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.petsService.deletePet(id).subscribe({
        next: () => {
          this.loadPets();
          Swal.fire('تم الحذف!', 'تم حذف الحيوان بنجاح.', 'success');
        },

        error: (err) => {
          console.error(err);
          Swal.fire('خطأ', 'حدث خطأ أثناء الحذف.', 'error');
        }
      });
    });
  }

  vaccinations(petId: number): void {
    this.router.navigate(['/petvaccinations'], { queryParams: { petId } });
  }

  markPetsWithUpcomingVaccinations(): void {
    const warehouseId = Number(localStorage.getItem('posId'));
    if (!warehouseId) return;

    this.petsService.getUpcomingVaccinations(warehouseId).subscribe({
      next: (res) => {
        const upcoming = res.data || res || [];
        const ids = new Set(upcoming.map((v: any) => v.pet_id));

        this.allData = this.allData.map((pet: any) => ({
          ...pet,
          hasUpcomingVaccination: ids.has(pet.id)
        }));

        this.refreshVisiblePetsAfterDataUpdate();
      },

      error: (err) => console.error(err)
    });
  }

  refreshVisiblePetsAfterDataUpdate(): void {
    const visibleIds = new Set(this.visiblePets.map((pet: any) => pet.id));
    this.visiblePets = this.allData.filter((pet: any) => visibleIds.has(pet.id));
  }

  resetForm(): void {
    this.petForm.reset({
      pet_type: '',
      name: '',
      breed: '',
      color: '',
      special_markings: '',
      date_of_birth: '',
      gender: 'male',
      spayed_neutered: false
    });

    this.editMode = false;
    this.selectedPetId = null;
  }

  getPetTypeLabel(petType: string | number): string {
    const labels: Record<string, string> = {
      '0': this.transloco.translate('cats'),
      '1': this.transloco.translate('dogs'),
      '2': this.transloco.translate('birds'),
      '4': this.transloco.translate('cats&dogs'),
      '3': this.transloco.translate('other')
    };

    return labels[petType?.toString()] || '—';
  }

  getPetTypeClass(petType: string | number): string {
    const classes: Record<string, string> = {
      '0': 'cats',
      '1': 'dogs',
      '2': 'birds',
      '4': 'cats-dogs',
      '3': 'other'
    };

    return classes[petType?.toString()] || 'other';
  }

  getPetInitial(pet: any): string {
    const name = (pet?.name || '').trim();
    return name ? name.charAt(0).toUpperCase() : '🐾';
  }

  trackByPetId(index: number, pet: any): number {
    return pet?.id || index;
  }
}
